// frontend/src/pages/CustomizationPage.tsx
// Admin UI for managing organization custom fields and grid configuration

import React, { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import apiClient from '@/api';

interface CustomField {
  fieldId: string;
  name: string;
  type: string;
  required?: boolean;
  description?: string;
  options?: string[];
  source?: 'template' | 'organization';
}

type TabType = 'correspondence' | 'project' | 'grid';

const FIELD_TYPES = ['text', 'number', 'date', 'boolean', 'url', 'currency', 'email', 'phone', 'select', 'multi-select'];

export const CustomizationPage: React.FC = () => {
  const { user } = useAuthStore();
  const { addNotification } = useAppStore();

  const [activeTab, setActiveTab] = useState<TabType>('correspondence');
  const [customization, setCustomization] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add field modal
  const [showAddField, setShowAddField] = useState(false);
  const [addFieldType, setAddFieldType] = useState<'correspondenceFields' | 'projectFields'>('correspondenceFields');
  const [newField, setNewField] = useState({ fieldId: '', name: '', type: 'text', required: false, description: '', options: '' });
  const [addingField, setAddingField] = useState(false);

  // Grid config
  const [gridConfig, setGridConfig] = useState<any>(null);
  const [gridLoading, setGridLoading] = useState(false);

  const fetchCustomization = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.organizationCustomization.getCustomization();
      setCustomization((result as any)?.data || result);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setCustomization(null); // No customization yet
      } else {
        setError(err?.response?.data?.error || err.message || 'Failed to load customization');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGridConfig = useCallback(async (gridType: string) => {
    setGridLoading(true);
    try {
      const result = await apiClient.organizationCustomization.getGridConfig(gridType);
      setGridConfig((result as any)?.data || result);
    } catch {
      setGridConfig(null);
    } finally {
      setGridLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomization();
  }, [fetchCustomization]);

  useEffect(() => {
    if (activeTab === 'grid') {
      fetchGridConfig('correspondenceGrid');
    }
  }, [activeTab, fetchGridConfig]);

  const handleAddField = async () => {
    if (!newField.fieldId || !newField.name || !newField.type) return;
    setAddingField(true);
    try {
      const fieldData: any = {
        fieldId: newField.fieldId,
        name: newField.name,
        type: newField.type,
        required: newField.required,
        description: newField.description || undefined,
      };
      if (['select', 'multi-select'].includes(newField.type) && newField.options) {
        fieldData.options = newField.options.split(',').map(o => o.trim()).filter(Boolean);
      }
      await apiClient.organizationCustomization.addField(addFieldType, fieldData);
      addNotification?.({ type: 'success', title: 'Field Added', message: `Custom field "${newField.name}" added successfully.` });
      setShowAddField(false);
      setNewField({ fieldId: '', name: '', type: 'text', required: false, description: '', options: '' });
      fetchCustomization();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Failed', message: err?.response?.data?.error || err.message });
    } finally {
      setAddingField(false);
    }
  };

  const handleDeleteField = async (fieldType: string, fieldId: string) => {
    if (!confirm(`Delete custom field "${fieldId}"?`)) return;
    try {
      await apiClient.organizationCustomization.deleteField(fieldType, fieldId);
      addNotification?.({ type: 'success', title: 'Field Deleted', message: `Field "${fieldId}" removed.` });
      fetchCustomization();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Failed', message: err?.response?.data?.error || err.message });
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'implementor')) {
    return (
      <PageLayout title="Customization" subtitle="Access denied">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Only administrators can access customization settings.</p>
        </div>
      </PageLayout>
    );
  }

  const getEffectiveFields = (fieldType: 'correspondenceFields' | 'projectFields'): CustomField[] => {
    if (!customization) return [];
    const fields: CustomField[] = [];

    // Template fields
    const templateFields = customization.baseTemplate?.[fieldType] || [];
    templateFields.forEach((f: any) => fields.push({ ...f, source: 'template' }));

    // Additional org fields
    const additionalKey = fieldType === 'correspondenceFields' ? 'additionalCorrespondenceFields' : 'additionalProjectFields';
    const additionalFields = customization[additionalKey] || [];
    additionalFields.forEach((f: any) => fields.push({ ...f, source: 'organization' }));

    return fields;
  };

  const renderFieldTable = (fieldType: 'correspondenceFields' | 'projectFields', label: string) => {
    const fields = getEffectiveFields(fieldType);

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">{label}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{fields.length} field{fields.length !== 1 ? 's' : ''} configured</p>
          </div>
          <Button
            variant="primary"
            onClick={() => { setAddFieldType(fieldType); setShowAddField(true); }}
          >
            + Add Field
          </Button>
        </div>

        {fields.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No custom fields configured. Click "Add Field" to create one, or apply an industry template.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Field ID</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Required</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Source</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {fields.map((field) => (
                  <tr key={field.fieldId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">{field.fieldId}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{field.name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">{field.type}</span>
                    </td>
                    <td className="px-4 py-3">{field.required ? '✓' : '-'}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'px-2 py-0.5 rounded text-xs',
                        field.source === 'template' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      )}>
                        {field.source === 'template' ? 'Template' : 'Custom'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {field.source === 'organization' && (
                        <button
                          onClick={() => handleDeleteField(fieldType, field.fieldId)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout title="Customization" subtitle="Manage custom fields and grid configuration">
      <div className="space-y-6">
        {error && <ErrorAlert message={error} onClose={() => setError(null)} />}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px space-x-6">
            {[
              { key: 'correspondence' as const, label: 'Correspondence Fields' },
              { key: 'project' as const, label: 'Project Fields' },
              { key: 'grid' as const, label: 'Grid Configuration' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'py-3 px-1 border-b-2 text-sm font-medium transition-colors',
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
        ) : (
          <>
            {/* Template Info */}
            {customization?.baseTemplate && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <span className="text-lg">📋</span>
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      Template: {customization.baseTemplate.name}
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Industry: <span className="capitalize">{customization.baseTemplate.industry}</span>
                      {' · '}Version: {typeof customization.templateVersionAtSelection === 'object' && customization.templateVersionAtSelection
                        ? `${customization.templateVersionAtSelection.major || 0}.${customization.templateVersionAtSelection.minor || 0}.${customization.templateVersionAtSelection.patch || 0}`
                        : (customization.templateVersionAtSelection || 'N/A')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!customization && !error && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">No customization configured yet</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Go to Industry Templates to apply a template, or start adding custom fields below.
                </p>
              </div>
            )}

            {activeTab === 'correspondence' && renderFieldTable('correspondenceFields', 'Correspondence Custom Fields')}
            {activeTab === 'project' && renderFieldTable('projectFields', 'Project Custom Fields')}

            {activeTab === 'grid' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Grid Configuration</h2>
                {gridLoading ? (
                  <div className="flex justify-center py-8"><LoadingSpinner /></div>
                ) : gridConfig ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Configure which columns appear in the correspondences grid.
                    </p>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Column</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Field</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Width</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Visible</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {(gridConfig.columns || []).map((col: any, i: number) => (
                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="px-4 py-3 text-gray-900 dark:text-white">{col.header || col.name}</td>
                              <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">{col.field || col.accessorKey}</td>
                              <td className="px-4 py-3 text-gray-500">{col.width || col.size || '-'}</td>
                              <td className="px-4 py-3">{col.visible !== false ? '✓' : '✗'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No grid configuration found. Default columns will be used.</p>
                )}
              </div>
            )}
          </>
        )}

        {/* Add Field Modal */}
        {showAddField && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add Custom Field ({addFieldType === 'correspondenceFields' ? 'Correspondence' : 'Project'})
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Field ID *</label>
                <input
                  type="text"
                  placeholder="e.g., contract_number"
                  value={newField.fieldId}
                  onChange={(e) => setNewField(prev => ({ ...prev, fieldId: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and underscores only</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Contract Number"
                  value={newField.name}
                  onChange={(e) => setNewField(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
                <select
                  value={newField.type}
                  onChange={(e) => setNewField(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {['select', 'multi-select'].includes(newField.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Options (comma-separated)</label>
                  <input
                    type="text"
                    placeholder="e.g., Option 1, Option 2, Option 3"
                    value={newField.options}
                    onChange={(e) => setNewField(prev => ({ ...prev, options: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="Optional description"
                  value={newField.description}
                  onChange={(e) => setNewField(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="fieldRequired"
                  checked={newField.required}
                  onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="fieldRequired" className="text-sm text-gray-700 dark:text-gray-300">Required field</label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowAddField(false)}>Cancel</Button>
                <Button onClick={handleAddField} disabled={addingField || !newField.fieldId || !newField.name}>
                  {addingField ? <><LoadingSpinner size="sm" /> Adding...</> : 'Add Field'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
